import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('loading');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d9p0ps2qs38sj6jc7h6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-100/70 py-16 md:py-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于汗汗漫画网
            </h1>
            <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              专注收录好看的韩国漫画资源，致力于为广大漫迷提供优质的韩漫在线免费阅读服务
            </p>
          </div>
        </section>

        <nav className="max-w-[1280px] mx-auto px-4 md:px-6 pt-6" aria-label="Breadcrumb">
          <ol className="flex items-center gap-1.5 text-sm">
            <li>
              <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            </li>
            <li className="text-foreground-300">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            <li>
              <span className="text-foreground-700 font-medium">关于我们</span>
            </li>
          </ol>
        </nav>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                品牌介绍
              </h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  汗汗漫画网创立于2020年，是国内领先的<strong>韩国漫画</strong>在线阅读平台。我们致力于为中文读者提供最新、最全的韩漫资源，让每一位漫画爱好者都能在这里找到自己喜爱的作品。
                </p>
                <p>
                  平台目前收录超过<strong>1000部</strong>优质韩国漫画作品，涵盖少年漫画、少女漫画、耽美漫画、奇幻漫画、悬疑漫画、喜剧漫画、动作漫画、恐怖漫画等多种类型。无论是热血战斗还是甜蜜恋爱，总有一部作品能够触动你的心弦。
                </p>
                <p>
                  汗汗漫画网坚持<strong>免费阅读</strong>的理念，所有收录的漫画作品均支持在线免费阅读，无需注册、无需付费。我们相信，好的作品应该被更多人看到，优质的内容不应该被付费壁垒所阻隔。
                </p>
                <p>
                  我们的编辑团队每天精心筛选和更新最新<strong>韩漫</strong>章节，确保读者能够第一时间追更自己喜欢的作品。同时，我们也在不断优化网站体验，让阅读变得更加舒适和便捷。
                </p>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20cozy%20digital%20comic%20reading%20space%20with%20large%20monitor%20displaying%20colorful%20manhwa%20pages%20warm%20ambient%20lighting%20bookshelves%20with%20comic%20collections%20in%20background%20minimalist%20interior%20design%20soft%20natural%20light%20from%20window%20Korean%20aesthetic%20vibe&width=800&height=600&seq=about-brand-01&orientation=landscape"
                alt="汗汗漫画网品牌形象"
                title="汗汗漫画网 - 韩国漫画在线阅读平台"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </section>

        <section className="bg-background-100/70 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-3">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8 max-w-lg mx-auto">
              来自广大读者的真实评价，是我们不断进步的动力
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '小王同学',
                  rating: 5,
                  comment: '汗汗漫画网真的超级好用！更新速度快，漫画资源也很全。我最喜欢的几部韩国漫画在这里都能找到，而且完全免费，强烈推荐给所有喜欢看韩漫的朋友！',
                  date: '2026年7月',
                },
                {
                  name: '漫画迷小李',
                  rating: 5,
                  comment: '用了很多漫画网站，汗汗漫画网是我最满意的。界面简洁好看，没有乱七八糟的广告，阅读体验非常好。分类也很清晰，找漫画很方便。',
                  date: '2026年6月',
                },
                {
                  name: '韩漫爱好者',
                  rating: 4,
                  comment: '作为一个重度韩漫读者，汗汗漫画网的更新速度让我非常满意。基本上热门漫画都能同步更新，而且画质清晰。希望以后能收录更多冷门好作品！',
                  date: '2026年7月',
                },
                {
                  name: '天天追漫',
                  rating: 5,
                  comment: '从朋友推荐过来用的，一用就停不下来了。漫画库真的很全，从完结到连载都有。手机上看也很方便，随时随地追更。',
                  date: '2026年5月',
                },
                {
                  name: '深夜读者',
                  rating: 5,
                  comment: '每天晚上都要来汗汗漫画网看看有没有更新。分类做得很好，耽美漫画区质量特别高。希望平台越来越好，收录更多优质韩漫！',
                  date: '2026年7月',
                },
                {
                  name: '二次元少女',
                  rating: 4,
                  comment: '超喜欢汗汗漫画网的少女漫画区！画面精美，剧情好看。每天都有新漫画更新，追更追得停不下来。已经推荐给身边所有喜欢看漫画的朋友了。',
                  date: '2026年6月',
                },
              ].map((review, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, starIdx) => (
                      <i
                        key={starIdx}
                        className={`text-sm ${starIdx < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-200'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.comment}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                    <span className="text-xs text-foreground-400">{review.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                联系方式
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">contact@hanhanmanhua.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">微信公众号</h4>
                    <p className="text-sm text-foreground-500">汗汗漫画官方</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-question-answer-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">反馈建议</h4>
                    <p className="text-sm text-foreground-500">
                      如果您有任何建议或意见，欢迎通过以上方式联系我们。我们会在24小时内回复您的来信。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-background-100 rounded-xl p-6 md:p-8">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-4">给我们留言</h3>
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="space-y-4"
                data-readdy-form="true"
              >
                <div>
                  <label htmlFor="about_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的称呼
                  </label>
                  <input
                    type="text"
                    id="about_name"
                    name="name"
                    className="w-full text-sm bg-background-50 border border-background-200 rounded-lg px-4 py-2.5 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的称呼"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="about_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    type="email"
                    id="about_email"
                    name="email"
                    className="w-full text-sm bg-background-50 border border-background-200 rounded-lg px-4 py-2.5 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="about_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="about_message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full text-sm bg-background-50 border border-background-200 rounded-lg px-4 py-2.5 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                    required
                  ></textarea>
                </div>
                <div className="hp-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'loading' ? '发送中...' : '发送留言'}
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 text-center">留言发送成功！感谢您的反馈。</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <style>{`
        .hp-field { position: absolute; left: -9999px; opacity: 0; height: 0; width: 0; overflow: hidden; }
        .hp-input { display: none; }
      `}</style>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于汗汗漫画网 - 韩国漫画在线阅读平台',
            description: '汗汗漫画网专注收录好看的韩国漫画资源，提供韩漫在线免费阅读服务。品牌介绍、用户口碑及联系方式。',
            url: 'https://hanhanmanhua.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '汗汗漫画网',
              url: 'https://hanhanmanhua.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanhanmanhua.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '汗汗漫画网',
            url: 'https://hanhanmanhua.com/',
            description: '专注韩国漫画在线免费阅读平台',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'contact@hanhanmanhua.com',
              contactType: 'customer service',
            },
          }),
        }}
      />
    </div>
  );
}